/**
 * Workbench — a reusable master-detail layout primitive for v2 views
 * (Code, Database, and later views instantiate this rather than each
 * hand-rolling the rail/detail/drawer chrome).
 *
 * createWorkbench(mount, config) builds:
 *   .content-head (title/sub/actions)
 *   .wb (flex row) > .wb-rail (segments + filter + list) + .wb-detail + .wb-drawer
 *
 * config = {
 *   header: { title, sub, actions? },
 *   segments?: [{ id, label }],
 *   onSegment?(segId),
 *   renderRow(item) -> htmlString   // outer element MUST carry data-id + data-seg
 *   onSelect(item, detailApi),
 *   drawer?: { title, render(ctx, body) },
 * }
 *
 * Returned handle: { setList, select, getSelectedId, filterBySegment,
 *   openDrawer, closeDrawer, destroy }
 */
import { esc, escAttr } from "./state.js";
import { iconHtml } from "./icons.js";

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested directly, no DOM required)
// ---------------------------------------------------------------------------

export function filterItems(items, query, nameOf) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return items;
  return items.filter(it => String(nameOf(it) || "").toLowerCase().includes(q));
}

export function rowIsSelected(rowEl, id) {
  return rowEl?.getAttribute("data-id") === id;
}

// ---------------------------------------------------------------------------
// DOM factory
// ---------------------------------------------------------------------------

export function createWorkbench(mount, config) {
  const segments = config.segments || null;
  const seg = segments
    ? `<div class="seg2 wb-seg">${segments.map((s, i) =>
        `<button type="button" class="${i === 0 ? "on" : ""}" data-seg="${escAttr(s.id)}">${esc(s.label)}</button>`).join("")}</div>`
    : "";

  mount.innerHTML = `
    <div class="content-head">
      <span class="ch-title">${esc(config.header.title)}</span>
      <span class="ch-sub">${esc(config.header.sub || "")}</span>
      <div class="ch-right" data-wb-actions></div>
    </div>
    <div class="wb">
      <aside class="wb-rail">
        <div class="wb-rail-head">
          ${seg}
          <span class="field wb-filter">${iconHtml("search", { size: "sm" })}
            <input type="text" class="wb-filter-input" placeholder="Filter…" autocomplete="off" /></span>
        </div>
        <div class="wb-list" role="listbox" tabindex="0"></div>
      </aside>
      <section class="wb-detail">
        <div class="wb-detail-head"></div>
        <div class="wb-detail-body"><div class="wb-detail-empty">Select an item to view it.</div></div>
      </section>
      <aside class="wb-drawer" hidden><div class="wb-drawer-head"></div><div class="wb-drawer-body"></div></aside>
    </div>`;

  const listEl = mount.querySelector(".wb-list");
  const filterInputEl = mount.querySelector(".wb-filter-input");
  const detailHeadEl = mount.querySelector(".wb-detail-head");
  const detailBodyEl = mount.querySelector(".wb-detail-body");
  const actionsEl = mount.querySelector("[data-wb-actions]");
  const drawerEl = mount.querySelector(".wb-drawer");
  const drawerHeadEl = mount.querySelector(".wb-drawer-head");
  const drawerBodyEl = mount.querySelector(".wb-drawer-body");
  const segEl = mount.querySelector(".wb-seg");

  if (config.header.actions) {
    _setNodeOrHtml(actionsEl, config.header.actions);
  }

  let allItems = [];
  let activeSegId = segments && segments.length ? segments[0].id : null;
  let query = "";
  let selectedId = null;

  const detailApi = {
    setBody(nodeOrHtml) { _setNodeOrHtml(detailBodyEl, nodeOrHtml); },
    setHead(nodeOrHtml) { _setNodeOrHtml(detailHeadEl, nodeOrHtml); },
    setActions(nodeOrHtml) { _setNodeOrHtml(actionsEl, nodeOrHtml); },
  };

  function _setNodeOrHtml(target, nodeOrHtml) {
    if (!target) return;
    if (nodeOrHtml == null) { target.innerHTML = ""; return; }
    if (typeof nodeOrHtml === "string") {
      target.innerHTML = nodeOrHtml;
    } else {
      target.innerHTML = "";
      target.appendChild(nodeOrHtml);
    }
  }

  // Rows visible under the current segment (data-seg match), independent of
  // the text filter — this is what filterBySegment()'s empty-state check and
  // keyboard navigation both need to agree on.
  function _segRows() {
    const rows = Array.from(listEl.querySelectorAll(".wb-row"));
    if (!activeSegId) return rows;
    return rows.filter(r => r.getAttribute("data-seg") === activeSegId);
  }

  // Rows visible under BOTH the current segment and the text filter — i.e.
  // every row not hidden via [hidden].
  function _visibleRows() {
    return _segRows().filter(r => !r.hasAttribute("hidden"));
  }

  function _renderList() {
    const filtered = typeof config.onFilter === "function"
      ? config.onFilter(allItems, query)
      : filterItems(allItems, query, it => (it.name != null ? it.name : it.label));
    if (!filtered.length) {
      listEl.innerHTML = `<div class="wb-empty">No matches.</div>`;
      return;
    }
    listEl.innerHTML = filtered.map(it => config.renderRow(it)).join("");
    _applySegmentVisibility();
    _reflectSelection();
  }

  function _applySegmentVisibility() {
    const rows = Array.from(listEl.querySelectorAll(".wb-row"));
    let anyVisible = false;
    for (const r of rows) {
      const matches = !activeSegId || r.getAttribute("data-seg") === activeSegId;
      if (matches) { r.removeAttribute("hidden"); anyVisible = true; }
      else r.setAttribute("hidden", "");
    }
    let emptyEl = listEl.querySelector(".wb-empty-seg");
    if (!anyVisible && rows.length) {
      if (!emptyEl) {
        emptyEl = document.createElement("div");
        emptyEl.className = "wb-empty wb-empty-seg";
        emptyEl.textContent = "No matches.";
        listEl.appendChild(emptyEl);
      }
    } else if (emptyEl) {
      emptyEl.remove();
    }
  }

  function _reflectSelection() {
    const rows = Array.from(listEl.querySelectorAll(".wb-row"));
    for (const r of rows) {
      r.setAttribute("aria-selected", rowIsSelected(r, selectedId) ? "true" : "false");
    }
  }

  function _itemById(id) {
    return allItems.find(it => String(it.id) === String(id));
  }

  function select(id) {
    selectedId = id;
    _reflectSelection();
    const item = _itemById(id);
    if (item && typeof config.onSelect === "function") {
      config.onSelect(item, detailApi);
    }
  }

  function getSelectedId() {
    return selectedId;
  }

  function setList(items) {
    allItems = items || [];
    if (selectedId != null && !_itemById(selectedId)) {
      selectedId = null;
      detailApi.setBody(`<div class="wb-detail-empty">Select an item to view it.</div>`);
      detailApi.setHead("");
    }
    _renderList();
  }

  function filterBySegment(segId) {
    activeSegId = segId;
    if (segEl) {
      Array.from(segEl.querySelectorAll("button")).forEach(btn => {
        btn.classList.toggle("on", btn.getAttribute("data-seg") === segId);
      });
    }
    _applySegmentVisibility();
    _reflectSelection();
  }

  function openDrawer(ctx) {
    if (!config.drawer) return;
    drawerEl.removeAttribute("hidden");
    drawerHeadEl.innerHTML = `<span class="ch-title">${esc(config.drawer.title || "")}</span>`;
    drawerBodyEl.innerHTML = "";
    config.drawer.render(ctx, drawerBodyEl);
  }

  function closeDrawer() {
    drawerEl.setAttribute("hidden", "");
    drawerHeadEl.innerHTML = "";
    drawerBodyEl.innerHTML = "";
  }

  function isDrawerOpen() {
    return !drawerEl.hasAttribute("hidden");
  }

  // ---- Event wiring ---------------------------------------------------

  function _onListClick(e) {
    const row = e.target.closest(".wb-row");
    if (!row || !listEl.contains(row)) return;
    select(row.getAttribute("data-id"));
  }

  function _onListKeydown(e) {
    if (e.key === "/") {
      e.preventDefault();
      filterInputEl.focus();
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "Enter") return;
    const visible = _visibleRows();
    if (!visible.length) return;
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const curIdx = visible.findIndex(r => rowIsSelected(r, selectedId));
      let nextIdx;
      if (curIdx === -1) nextIdx = e.key === "ArrowDown" ? 0 : visible.length - 1;
      else nextIdx = e.key === "ArrowDown" ? Math.min(curIdx + 1, visible.length - 1) : Math.max(curIdx - 1, 0);
      const nextRow = visible[nextIdx];
      select(nextRow.getAttribute("data-id"));
      nextRow.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedId == null && visible.length) {
        select(visible[0].getAttribute("data-id"));
      } else if (selectedId != null) {
        // Mirror a mouse click on the already-selected row -- open the ROW
        // DETAIL (config.onSelect), not the assistant drawer. This used to
        // call openDrawer({id, item}) with
        // no kind/prompt, which assistant-drawer.js's renderAssistant() reads
        // as an explain/improve ctx with an undefined prompt -- sendComposerText(undefined)
        // then either no-ops or (worse, if a run happens to already be in
        // flight) surfaces a misleading "Agent is busy" state that has
        // nothing to do with what the user just did.
        select(selectedId);
      }
    }
  }

  function _onFilterInput(e) {
    query = e.target.value;
    _renderList();
  }

  function _onFilterKeydown(e) {
    if (e.key === "Escape") {
      // Escape's first job is clearing an active filter (common convention).
      // Only when the filter is already empty does it bubble to the
      // mount-level handler below, which closes the drawer / clears selection.
      if (query) {
        query = "";
        filterInputEl.value = "";
        _renderList();
        e.preventDefault();
        e.stopPropagation();
      }
    } else if (e.key === "ArrowDown" || e.key === "Enter") {
      // Hand off to the list nav so filter -> Enter/Down feels continuous.
      e.preventDefault();
      listEl.focus();
      const visible = _visibleRows();
      if (visible.length) select(visible[0].getAttribute("data-id"));
    }
  }

  function _onSegClick(e) {
    const btn = e.target.closest("button[data-seg]");
    if (!btn || !segEl || !segEl.contains(btn)) return;
    const segId = btn.getAttribute("data-seg");
    filterBySegment(segId);
    if (typeof config.onSegment === "function") config.onSegment(segId);
  }

  // Mount-level: Escape closes the drawer if open, else clears selection --
  // regardless of which descendant (list or filter input) currently has
  // focus. The filter input handles its OWN Escape first (clear query) and
  // only lets the keystroke bubble here when its query is already empty.
  function _onMountKeydown(e) {
    if (e.key !== "Escape") return;
    if (isDrawerOpen()) { closeDrawer(); }
    else { selectedId = null; _reflectSelection(); }
    e.preventDefault();
  }

  listEl.addEventListener("click", _onListClick);
  listEl.addEventListener("keydown", _onListKeydown);
  filterInputEl.addEventListener("input", _onFilterInput);
  filterInputEl.addEventListener("keydown", _onFilterKeydown);
  if (segEl) segEl.addEventListener("click", _onSegClick);
  mount.addEventListener("keydown", _onMountKeydown);

  function destroy() {
    listEl.removeEventListener("click", _onListClick);
    listEl.removeEventListener("keydown", _onListKeydown);
    filterInputEl.removeEventListener("input", _onFilterInput);
    filterInputEl.removeEventListener("keydown", _onFilterKeydown);
    if (segEl) segEl.removeEventListener("click", _onSegClick);
    mount.removeEventListener("keydown", _onMountKeydown);
    mount.innerHTML = "";
  }

  return { setList, select, getSelectedId, filterBySegment, openDrawer, closeDrawer, destroy };
}
